import { useEffect } from "react";
import { Card, TextInput, Button, Label } from "flowbite-react";
import { useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { routes } from "../routes";
import useAuthors from "../hooks/useAuthors";
import { authorSchema } from "../schemas/author.schema";
import { useReducerAsState } from "../hooks/useReducerAsState";
import { Attachment } from "../types/attachment";
import { FileManager } from "../components";

interface AuthorState {
    attachment: Attachment | null;
    showFileManager: boolean;
}

const AuthorDetailsScreen = () => {
    const [state, setState] = useReducerAsState<AuthorState>({
        attachment: null,
        showFileManager: false,
    });
    const { showFileManager } = state;
    const { id } = useParams()
    const { loading, findAuthor, saveAuthor, author } = useAuthors()

    const { register, handleSubmit, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(authorSchema),
    });

    const navigate = useNavigate();

    const handleShowFileManager = () => setState({ showFileManager: !state.showFileManager });

    const handleSelectedFile = (file: Attachment) => {
        if(!file || file.type !== "image") {
            toast("Solamente se permiten imagenes para la imagen de la nota.",{
                type:"warning"
            })
            return
        }
        setState({attachment: file})
        setValue("thumbnail", file.id)
        handleShowFileManager() 
    }

    useEffect(() => {
        (async () => {
            if(!id) return

            const itemId = parseInt(id)
            if(!isNaN(itemId)){
                await findAuthor(itemId)
            }
        })();
        // eslint-disable-next-line
    },[id])

    useEffect(() => {
        if(!author) return;

        setValue("name", author.name)
        setValue("description", author.description)
        setValue("email", author.email)
        setValue("url", author.url)
        setValue("thumbnail", author.thumbnail ? author.thumbnail.id : 0)

        if(author.thumbnail){
            setState({attachment: author.thumbnail})
        }
        // eslint-disable-next-line
    },[author])

    const save = async (data: any) => {

        const info = {
            id: id ? parseInt(id) : undefined,
            name: data.name,
            description: data.description,
            email: data.email,
            url: data.url,
            thumbnail: data.thumbnail,
        }
       
        const saved = await saveAuthor(info)
        toast(saved ? "Autor guardado correctamente." : "Ocurrió un error al guardar el autor.",{
            type: saved ? "success" : "error"
        });

        if(saved && isNaN(parseInt(id!))){
            navigate(`${routes.DASHBOARD}/${routes.AUTHORS}`, {
                replace: true
            })
        }
    }

    return <div className="m-4">
        <div className="mb-4">
            <div className="flex flex-row justify-between">
                <h1>Detalles de autor</h1>
                <Button isProcessing={loading} disabled={loading} onClick={handleSubmit(save)}>Guardar autor</Button>
            </div>
        </div>
        <Card>
            <div className="flex flex-col mb-2">
                <Label className="mb-2">Nombre</Label>
                <TextInput 
                    placeholder="Escriba aquí"
                    {...register("name")}
                    helperText={errors && errors.name ? errors.name.message : ""}
                    required 
                    color={errors && errors.name ? "failure" : "gray"}
                />
            </div>
            <div className="flex flex-col mb-2">
                <Label className="mb-2">Descripción</Label>
                <TextInput 
                    placeholder="Escriba aquí"
                    {...register("description")}
                    helperText={errors && errors.description ? errors.description.message : ""}
                    required 
                    color={errors && errors.description ? "failure" : "gray"}
                />
            </div>
            <div className="flex flex-col mb-2">
                <Label className="mb-2">Email</Label>
                <TextInput 
                    placeholder="Escriba aquí"
                    {...register("email")}
                    helperText={errors && errors.email ? errors.email.message : ""}
                    required 
                    color={errors && errors.email ? "failure" : "gray"}
                />
            </div>
            <div className="flex flex-col mb-2">
                <Label className="mb-2">Enlace</Label>
                <TextInput 
                    placeholder="Escriba aquí"
                    {...register("url")}
                    helperText={errors && errors.url ? errors.url.message : ""}
                    required 
                    color={errors && errors.url ? "failure" : "gray"}
                />
            </div>

            <div id="fileUpload" className="max-w-md mb-4">
                        <div className="mb-2 block">
                            <Label htmlFor="file" value="Imagen del autor (1024 x 768)" />
                        </div>
                        {state.attachment && <Card
                            className="max-w-sm mb-4"
                                imgAlt={state.attachment?.foot}
                                imgSrc={`${process.env.REACT_APP_UPLOADS}/${state.attachment?.url}`}
                            >
                            <p className="font-normal text-gray-700 dark:text-gray-400">
                                {`${state.attachment?.source} ${state.attachment?.source !== "" && state.attachment?.foot !== "" ? "|" : ""} ${state.attachment?.foot}`}
                            </p>
                        </Card>
                        }
                        <Button color={state.attachment ? "failure" : "blue"} onClick={handleShowFileManager}>{state.attachment ? "Cambiar imagen" : "Elija una imagen"}</Button>
                        {errors && errors.thumbnail && <p className="mt-2 text-sm text-red-600 dark:text-red-500">{errors.thumbnail.message}</p>}
                    </div>
        </Card>

        {showFileManager && <FileManager open={showFileManager} onClose={handleShowFileManager} onSelect={handleSelectedFile} />}

    </div>
}


export default AuthorDetailsScreen